import { CircleAlert, File as FileIcon, Upload, X } from 'lucide-react';
import {
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type FocusEvent,
  type ReactNode,
} from 'react';
import { cn } from '../lib/cn';
import { focusRing } from '../lib/focus-ring';
import { useFieldContext, useFieldProps } from '../lib/field-context';

/* -------------------------------------------------------------------------- */
/* helpers                                                                     */
/* -------------------------------------------------------------------------- */

/** `xlsx` / `.XLSX` → `.xlsx`, so callers can write the list either way. */
function normaliseExtension(extension: string): string {
  const trimmed = extension.trim().toLowerCase();
  return trimmed.startsWith('.') ? trimmed : `.${trimmed}`;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;

  const units = ['KB', 'MB', 'GB'] as const;
  let value = bytes / 1024;
  let index = 0;

  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }

  const unit = units[index] ?? 'GB';
  const rounded = value >= 10 ? Math.round(value).toString() : value.toFixed(1).replace(/\.0$/, '');
  return `${rounded} ${unit}`;
}

function formatExtensionList(extensions: readonly string[]): string {
  const labels = extensions.map((extension) => normaliseExtension(extension).slice(1).toUpperCase());
  if (labels.length <= 1) return labels[0] ?? '';
  return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1] ?? ''}`;
}

/* -------------------------------------------------------------------------- */
/* component                                                                   */
/* -------------------------------------------------------------------------- */

export interface DropzoneProps {
  /** Called with the accepted file, or `null` when it is cleared. */
  onFileChange: (file: File | null) => void;
  /** Controlled selection. Leave undefined to let the Dropzone hold its own. */
  file?: File | null;
  /** Allowed extensions, with or without the dot. Empty means anything. */
  accept?: readonly string[];
  /** Largest accepted file, in bytes. */
  maxSizeBytes?: number;
  /** Notified when a file is refused, with the reason already made readable. */
  onReject?: (reason: string, file: File) => void;
  disabled?: boolean;
  /** The prompt inside the empty zone. */
  label?: ReactNode;
  /** Overrides the generated "XLSX or CSV · up to 20 MB" hint. */
  hint?: ReactNode;
  /** An error from outside — e.g. the server refused the upload. */
  error?: ReactNode;
  /** Name for the underlying file input, when posting a real form. */
  name?: string;
  id?: string;
  className?: string;
}

/**
 * Drag-and-drop plus click-to-browse, over a real `<input type="file">` so the
 * keyboard and screen-reader behaviour is the platform's own: Tab reaches it,
 * Enter or Space opens the picker, and the file name is announced.
 *
 * Rejections are never silent. A wrong extension or an oversized file produces
 * visible text in a `role="alert"`, and `onReject` fires with the same wording.
 */
export function Dropzone({
  onFileChange,
  file: controlledFile,
  accept = [],
  maxSizeBytes,
  onReject,
  disabled = false,
  label = 'Drag a file here, or browse',
  hint,
  error,
  name,
  id,
  className,
}: DropzoneProps) {
  const generatedId = useId();
  // Inside a <FormField> the input must own the field's id, or the field's
  // <Label htmlFor> points at nothing. Both labels then name the input, which
  // reads as "Contacts file, Drag a file here or browse" — correct, if verbose.
  const field = useFieldContext();
  const inputId = id ?? field?.id ?? `wend-dropzone-${generatedId}`;
  const hintId = `${inputId}-hint`;
  const alertId = `${inputId}-alert`;

  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);

  const [dragging, setDragging] = useState(false);
  const [focusVisible, setFocusVisible] = useState(false);
  const [rejection, setRejection] = useState<string | null>(null);
  const [internalFile, setInternalFile] = useState<File | null>(null);

  const file = controlledFile === undefined ? internalFile : controlledFile;
  const extensions = accept.map(normaliseExtension);
  const alert = rejection ?? error ?? null;

  const fieldProps = useFieldProps({
    id: inputId,
    'aria-describedby': alert === null ? hintId : `${hintId} ${alertId}`,
    'aria-invalid': alert === null ? undefined : true,
  });

  /** Returns a human reason, or null when the file is acceptable. */
  const validate = (candidate: File): string | null => {
    if (extensions.length > 0) {
      const lower = candidate.name.toLowerCase();
      const allowed = extensions.some((extension) => lower.endsWith(extension));
      if (!allowed) {
        return `“${candidate.name}” is not a supported file type. Choose a ${formatExtensionList(extensions)} file.`;
      }
    }

    if (maxSizeBytes !== undefined && candidate.size > maxSizeBytes) {
      return `“${candidate.name}” is ${formatFileSize(candidate.size)}. The limit is ${formatFileSize(maxSizeBytes)}.`;
    }

    return null;
  };

  const commit = (candidate: File | null) => {
    if (controlledFile === undefined) setInternalFile(candidate);
    onFileChange(candidate);
  };

  /** Single funnel for both sources of files: the picker and the drop. */
  const offer = (candidates: readonly File[]) => {
    // The input is always reset so re-picking the same file fires `change`.
    if (inputRef.current !== null) inputRef.current.value = '';

    const first = candidates[0];
    if (first === undefined) return;

    const reason = candidates.length > 1 ? 'Drop one file at a time.' : validate(first);
    if (reason !== null) {
      setRejection(reason);
      onReject?.(reason, first);
      return;
    }

    setRejection(null);
    commit(first);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    offer(Array.from(event.target.files ?? []));
  };

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    // Without preventDefault the browser navigates to the dropped file.
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    // Depth counting, because dragleave also fires when crossing into a child.
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    offer(Array.from(event.dataTransfer.files));
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    // The input is visually hidden and the ring is drawn on the zone, so
    // :focus-visible has to be read rather than styled: clicking the label
    // focuses the input too, and that click must not light the zone up.
    setFocusVisible(event.currentTarget.matches(':focus-visible'));
  };

  const clear = () => {
    if (inputRef.current !== null) inputRef.current.value = '';
    setRejection(null);
    commit(null);
    // Keyboard users land back on the control they just emptied.
    inputRef.current?.focus();
  };

  const constraints = [
    extensions.length > 0 ? formatExtensionList(extensions) : null,
    maxSizeBytes === undefined ? null : `up to ${formatFileSize(maxSizeBytes)}`,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-dragging={dragging ? '' : undefined}
        className={cn(
          'relative rounded-lg border border-dashed border-line-strong bg-white/[0.02]',
          'transition-[background-color,border-color] duration-150 ease-wend',
          dragging && 'border-brand-b bg-brand-a/10',
          alert !== null && 'border-bad/60',
          disabled && 'opacity-55',
          focusVisible && 'outline-2 outline-offset-2 outline-brand-b',
        )}
      >
        <input
          ref={inputRef}
          type="file"
          name={name}
          className="sr-only"
          accept={extensions.length > 0 ? extensions.join(',') : undefined}
          disabled={disabled}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={() => setFocusVisible(false)}
          {...fieldProps}
        />

        {file === null ? (
          <label
            htmlFor={inputId}
            className={cn(
              'flex w-full flex-col items-center gap-2 px-6 py-10 text-center',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
          >
            <Upload aria-hidden="true" className="size-5 text-brand-b" />
            <span className="text-[0.95rem] text-text">{label}</span>
            <span id={hintId} className="font-mono text-[0.7rem] uppercase tracking-[0.11em] text-muted">
              {hint ?? constraints}
            </span>
          </label>
        ) : (
          <div className="flex flex-wrap items-center gap-3 px-4 py-4">
            <FileIcon aria-hidden="true" className="size-5 shrink-0 text-brand-b" />

            <div className="min-w-0 flex-1">
              <p className="truncate text-[0.95rem] text-text" title={file.name}>
                {file.name}
              </p>
              <p
                id={hintId}
                className="font-mono text-[0.7rem] uppercase tracking-[0.11em] text-muted"
              >
                {formatFileSize(file.size)}
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {/* A second label for the same input: the picker opens, and the
                  input keeps one accessible name in this state. */}
              <label
                htmlFor={inputId}
                className={cn(
                  'inline-flex h-8 cursor-pointer items-center rounded-full border border-line-strong',
                  'bg-white/[0.04] px-3.5 text-[0.82rem] font-medium text-text',
                  'transition-colors duration-150 hover:border-white/25 hover:bg-white/[0.08]',
                  disabled && 'pointer-events-none',
                )}
              >
                Replace
              </label>

              <button
                type="button"
                onClick={clear}
                disabled={disabled}
                aria-label={`Remove ${file.name}`}
                className={cn(
                  'cursor-pointer rounded-sm p-1.5 text-muted transition-colors hover:text-text',
                  'disabled:cursor-not-allowed',
                  focusRing,
                )}
              >
                <X aria-hidden="true" className="size-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Permanent live region — the selection is announced without stealing
          focus, and it stays quiet while nothing is selected. */}
      <span role="status" aria-live="polite" className="sr-only">
        {file === null ? '' : `${file.name} selected, ${formatFileSize(file.size)}`}
      </span>

      {alert !== null && (
        <p
          id={alertId}
          role="alert"
          className="flex items-start gap-1.5 font-mono text-[0.72rem] leading-snug tracking-[0.04em] text-bad"
        >
          <CircleAlert aria-hidden="true" className="mt-px size-3.5 shrink-0" />
          <span>{alert}</span>
        </p>
      )}
    </div>
  );
}
